import { useState, useRef, useEffect, useMemo, useContext } from "react";
import {
  Box,
  Paper,
  InputBase,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Fade,
  Divider,
  ListItemIcon
} from "@mui/material";
import ApartmentIcon from '@mui/icons-material/Apartment';
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import useMutation from "../../../../shared/components/hooks/useMutation";
import useQuery from "../../../../shared/components/hooks/useQuery";
import { getLocalStorageString } from "../../../../shared/utils/localStorage";
import { extractNarration, getCommonStyles } from "../../../../shared/utils/prediction-utils";
import dayjs from "dayjs";
import { WhatIfChart } from "./WhatIfChart";
import { PredictionChart } from "./chatPredictionChart";
import { PropertyContext } from "../../../../contexts/PropertyContext";
import axios from "axios";

interface Message {
  id: number;
  type: "user" | "bot";
  text?: string;
  narration?: string;
  results?: any;
  results_by_property?: any;
  intent?: string;
  isError?: boolean;
}

interface PropertyOption {
  code: string;
  name: string;
  pmsList?: any[];
}

interface PredictionChatbotProps {
  selectedProperties: PropertyOption[];
  currentContext: {
    date: Date | null;
    forecastType: string;
    explanationType: string;
    kpis: string[];
    eventContext?: { name: string; date: string; endDate?: string } | null;
  }
}

export const PredictionChatbot = ({ selectedProperties, currentContext }: PredictionChatbotProps) => {

  const [chatQuery, setChatQuery] = useState("");
  const { pmsList } = useContext(PropertyContext);
  const theme = useTheme();

  const storedUserId = getLocalStorageString("userID") || "unknown_user";
  const storedCustomerId = getLocalStorageString("customerID")?.replace(/"/g, "") || "2";

  const [messages, setMessages] = useState<Message[]>([]);
  const { modifyData } = useMutation();

  type BotStatus = "idle" | "thinking" | "typing";
  const [status, setStatus] = useState<BotStatus>("idle");
  const [liveThinkingMessage, setLiveThinkingMessage] = useState("Connecting to assistant...");

  const abortControllerRef = useRef<AbortController | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);

  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [activeContextProperties, setActiveContextProperties] = useState<string[]>([]);

  useEffect(() => {
    if (selectedProperties && selectedProperties.length > 0) {
      const formatted = selectedProperties.map(p => `${p.code}:${p.name}`);
      setActiveContextProperties(formatted);
    }
  }, [selectedProperties]);

  const { data: propertiesData } = useQuery(
    `${process.env.REACT_APP_API_URL}/properties/my`,
    {},
    null,
    { enabled: true }
  );

  const allProperties: PropertyOption[] = useMemo(() => {
    const items = (propertiesData as any)?.items || [];
    return items.map((item: any) => ({
      code: item.property.code,
      name: item.property.name,
      pmsList: item.property.pmsList || []
    }));
  }, [propertiesData]);

  const filteredProperties = useMemo(() => {
    if (!mentionQuery) return allProperties;
    return allProperties.filter(p =>
      p.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
      p.code.toLowerCase().includes(mentionQuery.toLowerCase())
    );
  }, [allProperties, mentionQuery]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [messages, status]);

  const renderCharts = (results: any, intent?: string, showNarration: boolean = false) => {
    if (!results) return null;

    return Object.keys(results).map((kpiKey) => {
      const kpiData = results[kpiKey];

      if (!kpiData?.predictions || !Array.isArray(kpiData.predictions) || kpiData.predictions.length === 0) {
        return null;
      }

      const firstItem = kpiData.predictions[0];
      const isScenario = Object.keys(firstItem).some(k => k.startsWith('baseline_'));

      let ChartComponent = null;

      if (intent === "what-if" && isScenario) {
        ChartComponent = <WhatIfChart key={`${kpiKey}-chart`} data={kpiData.predictions} />;
      } else {
        ChartComponent = <PredictionChart key={`${kpiKey}-chart`} kpi={kpiKey === 'multi_kpi' ? 'revenue' : kpiKey} data={kpiData.predictions} />;
      }

      return (
        <Box key={kpiKey} sx={{ mb: 3 }}>
          {ChartComponent}

          {showNarration && kpiData.narration && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                {kpiData.narration}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mt: 3, borderStyle: 'dashed', borderColor: '#e0e0e0' }} />
        </Box>
      );
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    setChatQuery(val);
    const match = val.match(/@([^@]*)$/);
    if (match) {
      setShowMentions(true);
      setMentionQuery(match[1]);
    } else {
      setShowMentions(false);
    }
  };

  const handleSelectProperty = (property: PropertyOption) => {
    const newValue = chatQuery.replace(/@([^@]*)$/, `@${property.name} `);
    setChatQuery(newValue);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const simulateTyping = (messageId: number, fullNarration: string, finalMessageObj: Message) => {
    setStatus("typing");

    setMessages((prev) => {
      if (!prev.some(m => m.id === messageId)) {
        return [...prev, { ...finalMessageObj, narration: "" }];
      }
      const updated = [...prev];
      const idx = updated.findIndex((m) => m.id === messageId);
      updated[idx] = { ...finalMessageObj, narration: "" };
      return updated;
    });

    if (!fullNarration) {
      setStatus("idle");
      return;
    }

    let i = 0;
    if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);

    typingIntervalRef.current = setInterval(() => {
      setMessages((prev) => {
        const updated = [...prev];
        const idx = updated.findIndex((m) => m.id === messageId);
        if (idx !== -1) {
          updated[idx] = { ...updated[idx], narration: fullNarration.slice(0, i + 2) };
        }
        return updated;
      });
      i += 2;

      if (i >= fullNarration.length) {
        if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
        setStatus("idle");
      }
    }, 10);
  };

  const handleSend = async () => {
    if (!chatQuery.trim() || status !== "idle") return;

    const userText = chatQuery;
    const userMsgId = Date.now();
    const botMsgId = Date.now() + 1;

    setMessages((prev) => [...prev, { id: userMsgId, type: "user", text: userText }]);
    setChatQuery("");
    setShowMentions(false);

    setStatus("thinking");
    setLiveThinkingMessage("Initializing forecast...");
    abortControllerRef.current = new AbortController();

    const dateToSend = currentContext.date
      ? dayjs(currentContext.date).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    const mentionedObjects = allProperties.filter(prop =>
      userText.includes(`@${prop.name}`)
    );

    let selectedPropsPayload: string[] = [];
    let finalPmsList: any[] = [];

    if (mentionedObjects.length > 0) {
      selectedPropsPayload = mentionedObjects.map(p => `${p.code}:${p.name}`);
      const collectedPms = mentionedObjects.flatMap(p => p.pmsList || []);
      finalPmsList = Array.from(new Map(collectedPms.map((item: any) => [item.id, item])).values());
      setActiveContextProperties(selectedPropsPayload);
    } else {
      selectedPropsPayload = activeContextProperties.length > 0
        ? activeContextProperties
        : selectedProperties.map(p => `${p.code}:${p.name}`);

      const savedPmsStr = localStorage.getItem("selectedPmsList");
      const pmsFromStorage = savedPmsStr ? JSON.parse(savedPmsStr) : [];
      finalPmsList = (Array.isArray(pmsList) && pmsList.length > 0) ? pmsList : pmsFromStorage;
    }

    try {
      const payload = {
        user_id: storedUserId,
        customer_id: storedCustomerId,
        selected_properties: selectedPropsPayload,
        date: dateToSend,
        kpis: currentContext.kpis,
        forecast_type: currentContext.forecastType,
        explanation_type: currentContext.explanationType,
        user_query: userText,
        pms_list: finalPmsList,
        event_name: currentContext.eventContext?.name || null,
        event_start_date: currentContext.eventContext?.date || null,
        event_end_date: currentContext.eventContext?.endDate || null,
      };

      const data = await modifyData(
        `${process.env.REACT_APP_API_URL}/intelligence/forecast/stream-chat`,
        "post",
        payload,
        false,
        abortControllerRef.current.signal
      );

      const botMessageTemplate: Message = {
        id: botMsgId,
        type: "bot",
        narration: "",
      };

      if (data?.from_cache === true || !data?.job_id) {
        let narration = "";
        const isNestedStructure = data?.results && Object.keys(data.results).every(key => !isNaN(Number(key)));
        const isWhatIf = data?.intent === 'what-if';

        if (isWhatIf) {
          narration = "Here is the scenario impact analysis:";
        } else if (isNestedStructure || data?.results_by_property) {
          narration = "Here is the forecast for the selected properties:";
        } else {
          narration = extractNarration(data?.results) || data?.narration;
        }

        const finalMessageObj = {
          ...botMessageTemplate,
          results: data?.results,
          results_by_property: data?.results_by_property,
          intent: data?.intent,
        };

        simulateTyping(botMsgId, narration || "Forecast generated.", finalMessageObj);
        return;
      }

      let accumulatedNarration = "";
      let hasStartedStreaming = false;

      const sseUrl = `${process.env.REACT_APP_API_URL}/intelligence/forecast/stream/${data.job_id}`; const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      const closeStream = (forceIdle = true) => {
        es.close();
        eventSourceRef.current = null;
        if (forceIdle) {
          setStatus("idle");
        }
      };

      es.addEventListener("close", () => closeStream(true));

      es.addEventListener("message", (event: any) => {
        if (event.data === "[DONE]") {
          closeStream(true);
          return;
        }

        try {
          const streamData = JSON.parse(event.data);

          if (streamData.status && streamData.status !== "done" && streamData.message) {
            if (!hasStartedStreaming) {
              setLiveThinkingMessage(streamData.message);
            }
          }

          const liveToken = streamData.token !== undefined ? streamData.token : streamData.content;
          if (liveToken !== undefined) {
            if (liveToken.length > 30 && !hasStartedStreaming) { } else {
              if (!hasStartedStreaming) {
                hasStartedStreaming = true;
                setStatus("typing");
                setMessages((prev) => [...prev, botMessageTemplate]);
              }

              let tokenText = liveToken;
              try { tokenText = JSON.parse(tokenText); } catch (e) { }
              accumulatedNarration += tokenText;

              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findIndex((m) => m.id === botMsgId);
                if (idx !== -1) updated[idx] = { ...updated[idx], narration: accumulatedNarration };
                return updated;
              });
            }
          }

          const finalPayload = streamData.result || streamData;

          if (streamData.status === "done" || finalPayload.results !== undefined || finalPayload.results_by_property !== undefined) {

            let finalNarration = finalPayload.narration || accumulatedNarration;

            if (!finalNarration) {
              const isNested = finalPayload.results && Object.keys(finalPayload.results).every(key => !isNaN(Number(key)));
              if (finalPayload.intent === 'what-if') finalNarration = "Here is the scenario impact analysis:";
              else if (isNested || finalPayload.results_by_property) finalNarration = "Here is the forecast for the selected properties:";
              else finalNarration = extractNarration(finalPayload.results);
            }

            const finalMessageObj: Message = {
              ...botMessageTemplate,
              results: finalPayload.results,
              results_by_property: finalPayload.results_by_property,
              intent: finalPayload.intent
            };

            if (!hasStartedStreaming) {
              simulateTyping(botMsgId, finalNarration || "", finalMessageObj);
              closeStream(false);
            } else {
              setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findIndex((m) => m.id === botMsgId);
                if (idx !== -1) {
                  updated[idx] = { ...finalMessageObj, narration: finalNarration };
                }
                return updated;
              });
              closeStream(true);
            }
          }
        } catch (e) {
          if (typeof event.data === "string" && !event.data.includes("{")) {
            if (!hasStartedStreaming) {
              hasStartedStreaming = true;
              setStatus("typing");
              setMessages((prev) => [...prev, botMessageTemplate]);
            }
            accumulatedNarration += event.data;
            setMessages((prev) => {
              const updated = [...prev];
              const idx = updated.findIndex((m) => m.id === botMsgId);
              if (idx !== -1) updated[idx] = { ...updated[idx], narration: accumulatedNarration };
              return updated;
            });
          }
        }
      });

      es.onerror = (err) => {
        console.error("Forecast SSE Error:", err);
        if (accumulatedNarration.length === 0) {
          if (!hasStartedStreaming) {
            setMessages((prev) => [...prev, { ...botMessageTemplate, narration: "Error connecting to stream.", isError: true }]);
          } else {
            setMessages((prev) => {
              const updated = [...prev];
              const idx = updated.findIndex((m) => m.id === botMsgId);
              if (idx !== -1) updated[idx] = { ...updated[idx], narration: "Error connecting to stream.", isError: true };
              return updated;
            });
          }
        }
        closeStream(true);
      };

    } catch (error: any) {
      if (!axios.isCancel(error)) {
        setMessages((prev) => [
          ...prev,
          { id: Date.now(), type: "bot", narration: "Sorry, I couldn't process that.", isError: true },
        ]);
      }
      setStatus("idle");
      abortControllerRef.current = null;
    }
  };

  const commonStyle = getCommonStyles(theme);

  return (
    <Box className="chatbot-container" sx={{ mt: 2, position: 'relative' }}>
      <Box
        ref={listRef}
        className="message-list"
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          px: 0.5,
          py: 0.5,
        }}
      >
        {messages.map((msg, index) => {
          const isLastMessage = index === messages.length - 1;
          const isNestedStructure = msg.results && Object.keys(msg.results).every((key) => !isNaN(Number(key)));
          const isWhatIf = msg.intent === "what-if";

          if (msg.type === "user") {
            return (
              <Box
                key={msg.id}
                ref={isLastMessage && status !== "idle" ? lastMessageRef : null}
                sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}
              >
                <Box
                  sx={{
                    maxWidth: "70%",
                    backgroundColor: "#0a3a5c",
                    color: "#fff",
                    px: 2,
                    py: 1,
                    borderRadius: "16px 16px 4px 16px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography sx={{ fontSize: "0.875rem", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            );
          }

          return (
            <Box key={msg.id} ref={isLastMessage && status === "idle" ? lastMessageRef : null} sx={{ mb: 1.5 }}>
              <Accordion
                defaultExpanded
                className="response-accordion"
                elevation={0}
                sx={{
                  borderRadius: "14px !important",
                  overflow: "hidden",
                  backgroundColor: msg.isError ? "#ffebee" : "#fff",
                  border: msg.isError ? "1px solid #ef5350" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "black" }} />}
                  sx={{ minHeight: "44px !important", px: 1.5 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: msg.isError ? "error.main" : "text.primary",
                    }}
                  >
                    {msg.isError ? "Error" : msg.intent === "what-if" ? "Scenario Analysis" : "Response"}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                  {msg.narration && (
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.6,
                        fontSize: "0.875rem",
                        color: "#333",
                        mb: 1.5,
                      }}
                    >
                      {msg.narration}
                    </Typography>
                  )}

                  {!msg.isError && (
                    <Box mt={0.5}>
                      {isNestedStructure ? (
                        Object.keys(msg.results).map((propId) => {
                          const propData = msg.results[propId];
                          return (
                            <Accordion
                              key={propId}
                              elevation={0}
                              sx={{
                                border: "1px solid #e0e0e0",
                                mb: 1,
                                borderRadius: "10px !important",
                                "&:before": { display: "none" },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ bgcolor: "#f7f7f7", minHeight: "42px !important", borderRadius: "10px", px: 1.5 }}
                              >
                                <Typography fontWeight="600" fontSize="0.85rem">
                                  {propData.property_name || `Property ${propId}`}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 1.5 }}>
                                {propData.narration && (
                                  <Typography variant="body2" sx={{ mb: 1, fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>
                                    {propData.narration}
                                  </Typography>
                                )}
                                {renderCharts(propData.results, msg.intent, true)}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })
                      ) : msg.results_by_property ? (
                        Object.keys(msg.results_by_property).map((propId) => {
                          const propData = msg.results_by_property[propId];
                          return (
                            <Accordion
                              key={propId}
                              elevation={0}
                              sx={{
                                border: "1px solid #e0e0e0",
                                mb: 1,
                                borderRadius: "10px !important",
                                "&:before": { display: "none" },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ bgcolor: "#f7f7f7", minHeight: "42px !important", borderRadius: "10px", px: 1.5 }}
                              >
                                <Typography fontWeight="600" fontSize="0.85rem">
                                  {propData.property_name || `Property ${propId}`}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 1.5 }}>
                                {renderCharts(propData.results, msg.intent, true)}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })
                      ) : (
                        renderCharts(msg.results, msg.intent, isWhatIf)
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}

        {/* Dynamic Thinking/Loading Status */}
        {status === "thinking" && (
          <Box ref={lastMessageRef} sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "#f5f7f9",
                px: 1.8,
                py: 1,
                borderRadius: "16px 16px 16px 4px",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
              }}
            >
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                {liveThinkingMessage}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Input Area */}
      <Paper
        elevation={0}
        className="chat-paper"
        sx={{
          position: "relative",
          flexDirection: "column",
          alignItems: "stretch",
          borderRadius: "18px",
          p: 1,
          backgroundColor: "#fff",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Fade in={showMentions && filteredProperties.length > 0} timeout={200}>
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              bottom: "calc(100% + 8px)",
              left: 0,
              width: 300,
              maxHeight: 240,
              overflowY: "auto",
              zIndex: 1000,
              borderRadius: "10px",
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 10px 20px -4px rgba(0,0,0,0.15)",
              overflowX: "hidden",
            }}
          >
            <List dense sx={{ py: 0 }}>
              {filteredProperties.map((prop) => (
                <ListItem key={prop.code} disablePadding>
                  <ListItemButton
                    onClick={() => handleSelectProperty(prop)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderBottom: "1px solid #f5f5f5",
                      transition: "all 0.2s ease",
                      "&:hover": { bgcolor: "primary.50", pl: 2 },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: "primary.main" }}>
                      <ApartmentIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600} fontSize="0.85rem">{prop.name}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">Code: {prop.code}</Typography>}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              {filteredProperties.length === 0 && (
                <ListItem sx={{ py: 2, justifyContent: "center" }}>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic" fontSize="0.85rem">
                    No properties found
                  </Typography>
                </ListItem>
              )}
            </List>
          </Paper>
        </Fade>

        <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1.5, p: 0.5 }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="chat-form"
            sx={{
              flexGrow: 1,
              borderRadius: "16px",
              px: 1.5,
              minHeight: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
            }}
          >
            <InputBase
              inputRef={inputRef}
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              className="chat-input"
              placeholder="Type @ to select properties..."
              value={chatQuery}
              onChange={handleInputChange}
              disabled={status !== "idle"}
              sx={{ fontSize: "0.9rem", lineHeight: 1.4, borderRadius: "25px" }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (showMentions && filteredProperties.length > 0) {
                    handleSelectProperty(filteredProperties[0]);
                  } else {
                    handleSend();
                  }
                }
              }}
            />
          </Box>

          <Box className="actions-row">
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={status !== "idle" || !chatQuery.trim()}
              endIcon={status !== "idle" ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />}
              sx={{
                ...commonStyle,
                backgroundColor: theme.palette.primary.main,
                color: "white",
                border: "none",
                borderRadius: "25px",
                height: "44px",
                minWidth: "90px",
                px: 2,
                lineHeight: 1,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};